import { buildCustomDisciplineCompetitionView } from "src/shared/util/src/custom-disciplines/competition-view";
import { customDisciplineLabels } from "src/shared/util/src/custom-disciplines/labels";
import { useBehaviorTimerHost } from "./useBehaviorTimerHost";
import { getLanguage } from "src/shared/util/src/language/dico";
import {
  CUSTOM_DISCIPLINE_LIVE_EVENT,
  type CustomDisciplineLivePatch,
} from "src/shared/util/src/custom-disciplines/live-patch";
/**
 * Hook that collects web runtime data for public widget/display layouts and
 * exposes both the raw meet context and the final `InjectedData` payload used
 * by the compiled layout renderer.
 */

import { athletePowerscoutBests } from "@prisma-local/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { InjectedData } from "src/shared/ui/src/display-editor/layout-types";
import {
  type AthletePatch,
  athleteModelToAthleteWithAll,
  type AthletesModel,
  type AthleteWithAll,
  athleteWithAllToAthleteModel,
  type AttemptTimersUpdatePayload,
  type CurrentRefereeInfo,
  DEFAULTS,
  getAttemptNumber,
  getCurrentAthlete,
  getCurrentLift,
  getExercise,
  type GroupPatch,
  type GroupWithAll,
  getNextLiftDataWithGroupTransition,
  groupWithAllToGroupModel,
  getResultIfSuccessful,
  levelLocalToLevelRemote,
  type MeetWithAll,
  type MeetPatch,
  recordLocalToRecordRemote,
  type TimerUpdatePayload,
  type ThemeColors,
} from "src/shared/util/src";
import {
  buildInjectedDataFromNest,
  DEFAULT_USER_LAYOUT_DISCIPLINE_KEY,
  getCustomUserLayoutDisciplineKey,
  getStandardUserLayoutDisciplineKey,
  resolveCompetitionOrganisation,
} from "src/shared/util/src/layout";
import { useMeetSocketSync } from "src/shared/util/src/hooks/useMeetSocketSync";
import WebsocketClient from "src/shared/util/src/websocket-client";
import {
  buildCustomDisciplineLayoutData,
  type GroupWithCustomDisciplineRuntime,
} from "src/shared/util/src/custom-disciplines/layout-runtime";
import { useAgeCategoryStore } from "src/stores/ageCategoryStore";
import { useDisciplineStore } from "src/stores/disciplineStore";
import { useLevelStore } from "src/stores/levelStore";
import { useLigueStore } from "src/stores/ligueStore";
import { useMeetStore } from "src/stores/meetStore";
import { useCustomDisciplineStore } from "src/stores/customDisciplineStore";
import { useWeightCategoryStore } from "src/stores/weightCategory";

type UseWebLayoutDataOptions = {
  meetId: string;
  serverUrl?: string;
  token?: string;
  /** When false, skips HTTP/socket subscriptions until a layout is ready to render. */
  enabled?: boolean;
};

export type UseWebLayoutDataResult = {
  meet: ReturnType<typeof useMeetStore.getState>["meetsWithAll"][number] | null;
  currentThemeName: string | null;
  currentLayoutDisciplineKey: string;
  themeColors: ThemeColors;
  layoutData: InjectedData;
};

/**
 * Collects live meet state and auxiliary runtime events for the public
 * web-client layout renderer.
 *
 * Meet state is loaded from the live websocket snapshot only (no HTTP preload).
 * Auxiliary catalog stores are still fetched when empty for patch resolution.
 *
 * @param options - Meet ID, server URL and optional auth token.
 * @returns Meet context plus layout-ready injected data.
 */
export function useWebLayoutData(
  options: UseWebLayoutDataOptions,
): UseWebLayoutDataResult {
  const { meetId, serverUrl, token, enabled = true } = options;
  useBehaviorTimerHost(meetId, serverUrl, token, enabled);
  const meetsWithAll = useMeetStore((state) => state.meetsWithAll);
  const meet = useMemo(
    () => meetsWithAll.find((currentMeet) => currentMeet.id === meetId) ?? null,
    [meetId, meetsWithAll],
  );
  const updateMeetWithAll = useMeetStore((state) => state.updateMeetWithAll);
  const patchMeet = useMeetStore((state) => state.patchMeet);
  const deleteAthlete = useMeetStore((state) => state.deleteAthlete);
  const addGroup = useMeetStore((state) => state.addGroup);
  const deleteGroup = useMeetStore((state) => state.deleteGroup);
  const addAthlete = useMeetStore((state) => state.addAthlete);
  const selectGroup = useMeetStore((state) => state.selectGroup);
  const updatePowerscoutBests = useMeetStore(
    (state) => state.updatePowerscoutBests,
  );
  const { ligues, fetchLigues } = useLigueStore();
  const { disciplines, fetchDisciplines } = useDisciplineStore();
  const { weightcategories, fetchWeightCategories } = useWeightCategoryStore();
  const { agecategories, fetchAgeCategories } = useAgeCategoryStore();
  const { levels, fetchLevels } = useLevelStore();
  const {
    groupTypes: customDisciplineGroupTypes,
    labelTranslations,
    categoryReferences,
    fetchAll: fetchCustomDisciplines,
  } = useCustomDisciplineStore();
  const [currentAthleteFromSocket, setCurrentAthleteFromSocket] =
    useState<AthleteWithAll | null>(null);
  const [currentThemeName, setCurrentThemeName] = useState<string | null>(null);
  const [themeColors, setThemeColors] = useState<ThemeColors>(DEFAULTS);
  const [refereeLights, setRefereeLights] = useState<CurrentRefereeInfo | null>(
    null,
  );
  const [timerSeconds, setTimerSeconds] = useState(60);
  const [timer, setTimer] = useState("1:00");
  const [attemptTimers, setAttemptTimers] = useState<string[]>([]);
  const [attemptTimerSeconds, setAttemptTimerSeconds] = useState<number[]>([]);

  // Widget/display runtime pages rely on the live websocket snapshot only (no HTTP preload).

  useEffect(() => {
    if (!enabled) return;
    if (ligues.length === 0) void fetchLigues();
    if (disciplines.length === 0) void fetchDisciplines();
    if (weightcategories.length === 0) void fetchWeightCategories();
    if (agecategories.length === 0) void fetchAgeCategories();
    if (levels.length === 0) void fetchLevels();
    if (customDisciplineGroupTypes.length === 0) void fetchCustomDisciplines();
  }, [
    agecategories.length,
    customDisciplineGroupTypes.length,
    disciplines.length,
    enabled,
    fetchAgeCategories,
    fetchCustomDisciplines,
    fetchDisciplines,
    fetchLigues,
    fetchLevels,
    fetchWeightCategories,
    ligues.length,
    levels.length,
    weightcategories.length,
  ]);

  const onSnapshot = useCallback(
    (snapshot: MeetWithAll) => {
      updateMeetWithAll(snapshot, true);
    },
    [updateMeetWithAll],
  );

  const handlers = useMemo(
    () => ({
      "current-athlete": (payload: unknown) => {
        const currentAthletePayload =
          payload && typeof payload === "object" && "athlete" in payload
            ? (payload as { athlete?: AthletesModel | null }).athlete
            : null;
        setCurrentAthleteFromSocket(
          currentAthletePayload
            ? athleteModelToAthleteWithAll(currentAthletePayload)
            : null,
        );
      },
      "athlete-delete": (data: unknown) => {
        const { athleteId, groupId } = data as {
          athleteId: string;
          groupId: string;
        };
        deleteAthlete(meetId, athleteId, groupId);
      },
      "group-add": (group: unknown) => {
        addGroup(meetId, group as GroupWithAll);
      },
      "group-delete": (groupId: unknown) => {
        deleteGroup(meetId, groupId as string);
      },
      "athlete-add": (athlete: unknown) => {
        addAthlete(meetId, athlete as AthleteWithAll);
      },
      "meet-patch": (patch: unknown) => {
        void patchMeet(
          patch as MeetPatch,
          weightcategories,
          agecategories,
          disciplines,
          ligues,
        );
      },
      "athlete-patch": (patch: unknown) => {
        void patchMeet(
          patch as AthletePatch[],
          weightcategories,
          agecategories,
          disciplines,
          ligues,
        );
      },
      [CUSTOM_DISCIPLINE_LIVE_EVENT]: (patch: unknown) =>
        useMeetStore
          .getState()
          .patchCustomMeet(patch as CustomDisciplineLivePatch),
      "group-patch": (patch: unknown) => {
        void patchMeet(
          patch as GroupPatch,
          weightcategories,
          agecategories,
          disciplines,
          ligues,
        );
      },
      "select-group": (data: unknown) => {
        selectGroup(meetId, (data as { groupId: string }).groupId);
      },
      "powerscout-bests-update": (data: unknown) => {
        const { groupId, powerscoutBests } = data as {
          groupId: string;
          powerscoutBests: athletePowerscoutBests;
        };
        updatePowerscoutBests(meetId, groupId, powerscoutBests);
      },
    }),
    [
      addAthlete,
      addGroup,
      agecategories,
      deleteAthlete,
      deleteGroup,
      disciplines,
      ligues,
      meetId,
      patchMeet,
      selectGroup,
      updatePowerscoutBests,
      weightcategories,
    ],
  );

  useMeetSocketSync<MeetWithAll>({
    meetId: enabled ? meetId : "",
    token,
    websocketUrl: enabled ? (serverUrl ?? "") : "",
    liveOnly: true,
    liveSnapshotRetryMaxMs: Number.POSITIVE_INFINITY,
    onSnapshot,
    onThemeColors: setThemeColors,
    handlers,
  });

  useEffect(() => {
    if (!enabled || !serverUrl || !meetId) return;

    const wsClient = WebsocketClient.getInstance(serverUrl);
    const currentThemeEvent = `${meetId}/update-current-theme`;
    const timerUpdateEvent = `${meetId}/timerUpdate`;
    const attemptTimersEvent = `${meetId}/attemptTimersUpdate`;

    const handleCurrentTheme = (payload: unknown) => {
      setCurrentThemeName(typeof payload === "string" ? payload : null);
    };

    const handleTimerUpdate = (payload: unknown) => {
      const timerPayload = payload as TimerUpdatePayload | null;
      const timeLeft =
        typeof timerPayload?.timeLeft === "number"
          ? timerPayload.timeLeft
          : null;
      const formatted =
        typeof timerPayload?.formatted === "string"
          ? timerPayload.formatted
          : "";
      if (timeLeft === null) return;
      setTimerSeconds(timeLeft);
      setTimer(
        formatted
          ? formatted
          : `${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, "0")}`,
      );
    };

    const handleRefereeLights = (payload: unknown) => {
      setRefereeLights((payload as CurrentRefereeInfo | null) ?? null);
    };

    const handleAttemptTimersUpdate = (payload: unknown) => {
      const timers =
        (payload as AttemptTimersUpdatePayload | null)?.timers ?? [];
      const sortedTimers = [...timers].sort(
        (left, right) => right.id - left.id,
      );
      setAttemptTimerSeconds(sortedTimers.map((entry) => entry.remaining));
      setAttemptTimers(sortedTimers.map((entry) => entry.formatted));
    };

    const setup = () => {
      wsClient.off(currentThemeEvent, handleCurrentTheme);
      wsClient.off(timerUpdateEvent, handleTimerUpdate);
      wsClient.off(attemptTimersEvent, handleAttemptTimersUpdate);
      wsClient.off("currentRefereeInfo", handleRefereeLights);

      wsClient.on(currentThemeEvent, handleCurrentTheme);
      wsClient.on(timerUpdateEvent, handleTimerUpdate);
      wsClient.on(attemptTimersEvent, handleAttemptTimersUpdate);
      wsClient.on("currentRefereeInfo", handleRefereeLights);

      wsClient.emit("current-athlete", { id: meetId, auth_token: token });
      wsClient.emit("get-current-theme", { id: meetId });
    };

    if (!wsClient.isConnected()) {
      wsClient.connect(setup);
    } else {
      setup();
    }

    return () => {
      wsClient.off(currentThemeEvent, handleCurrentTheme);
      wsClient.off(timerUpdateEvent, handleTimerUpdate);
      wsClient.off(attemptTimersEvent, handleAttemptTimersUpdate);
      wsClient.off("currentRefereeInfo", handleRefereeLights);
    };
  }, [enabled, meetId, serverUrl, token]);

  const currentGroup = useMemo(
    () => meet?.groups.find((group) => group.selected) ?? null,
    [meet],
  );

  const athletes = useMemo(
    () => meet?.groups.flatMap((group) => group.athletes) ?? [],
    [meet],
  );

  const currentGroupAthleteModels = useMemo(
    () =>
      currentGroup?.athletes.map((athlete) =>
        athleteWithAllToAthleteModel(athlete),
      ) ?? [],
    [currentGroup],
  );

  const derivedCurrentAthlete = useMemo(() => {
    const currentAthleteModel =
      currentGroupAthleteModels.length > 0
        ? getCurrentAthlete(currentGroupAthleteModels)
        : null;
    if (!currentAthleteModel || !currentGroup) return null;
    return (
      currentGroup.athletes.find(
        (athlete) => athlete.id === currentAthleteModel.id,
      ) ?? null
    );
  }, [currentGroup, currentGroupAthleteModels]);

  /** Meet store (patches) is authoritative; socket snapshot fills gaps before first patch. */
  const currentAthlete = derivedCurrentAthlete ?? currentAthleteFromSocket;

  const currentLayoutDisciplineKey = useMemo(() => {
    const customGroup = currentGroup as
      | (GroupWithAll & {
          customDisciplineGroup?: {
            groupType?: { disciplineId?: string | null } | null;
          } | null;
          customDisciplineGroupId?: string | null;
        })
      | null;
    const customDisciplineId =
      customGroup?.customDisciplineGroup?.groupType?.disciplineId ?? null;
    if (customDisciplineId) {
      return getCustomUserLayoutDisciplineKey(customDisciplineId);
    }

    const format = String(currentGroup?.competitionFormat ?? "");
    if (format.startsWith("CUSTOM:")) {
      const groupTypeId = format.slice("CUSTOM:".length);
      const groupType = customDisciplineGroupTypes.find(
        (candidate) => candidate.id === groupTypeId,
      );
      if (groupType?.disciplineId) {
        return getCustomUserLayoutDisciplineKey(groupType.disciplineId);
      }
    }
    if (format.startsWith("custom-discipline:")) {
      const disciplineId = format.slice("custom-discipline:".length);
      if (disciplineId) {
        return getCustomUserLayoutDisciplineKey(disciplineId);
      }
    }

    const standardDisciplineId = currentAthlete?.disciplineId ?? null;
    return standardDisciplineId
      ? getStandardUserLayoutDisciplineKey(standardDisciplineId)
      : DEFAULT_USER_LAYOUT_DISCIPLINE_KEY;
  }, [currentAthlete, currentGroup, customDisciplineGroupTypes]);

  const schedulingView = useMemo(
    () =>
      buildCustomDisciplineCompetitionView(
        (meet?.groups ?? []).map((group) => groupWithAllToGroupModel(group)),
        athletes.map((athlete) => athleteWithAllToAthleteModel(athlete)),
        customDisciplineGroupTypes,
        {
          ageCategories: agecategories.map((row) => ({
            ...row,
            nom: row.name,
            nomRecord: row.recordName,
          })),
          weightCategories: weightcategories.map((row) => ({
            ...row,
            nom: row.name,
            poidMin: row.minWeight,
            poidMax: row.maxWeight,
          })),
          disciplines: disciplines.map((row) => ({ ...row, nom: row.name })),
          ...categoryReferences,
        } as never,
        { ...meet, startdate: meet?.startdate ?? new Date(0).toISOString() },
      ),
    [
      meet,
      athletes,
      customDisciplineGroupTypes,
      agecategories,
      weightcategories,
      disciplines,
      categoryReferences,
    ],
  );
  const allAthleteModels = schedulingView.athletes;

  const currentAthleteModel = useMemo(() => {
    if (currentGroupAthleteModels.length === 0) return null;
    if (currentAthlete) {
      const model = athleteWithAllToAthleteModel(currentAthlete);
      if (
        currentGroupAthleteModels.some((candidate) => candidate.id === model.id)
      ) {
        return model;
      }
    }
    return getCurrentAthlete(currentGroupAthleteModels);
  }, [currentAthlete, currentGroupAthleteModels]);

  const currentAthleteInGroup = useMemo(() => {
    if (!currentAthleteModel || !currentGroup) return null;
    return (
      currentGroup.athletes.find(
        (athlete) => athlete.id === currentAthleteModel.id,
      ) ?? null
    );
  }, [currentAthleteModel, currentGroup]);

  const currentLift = useMemo(
    () =>
      currentGroupAthleteModels.length > 0
        ? getCurrentLift(currentGroupAthleteModels)
        : null,
    [currentGroupAthleteModels],
  );

  const currentMovement = useMemo(() => {
    if (!currentLift) return null;
    return getExercise(currentLift as never) as "squat" | "bench" | "deadlift";
  }, [currentLift]);

  const attemptNumber = useMemo(
    () => (currentLift ? getAttemptNumber(currentLift as never) : undefined),
    [currentLift],
  );

  const placementIfSuccessful = useMemo(() => {
    if (!currentAthleteModel || !currentLift || allAthleteModels.length === 0) {
      return null;
    }

    try {
      return getResultIfSuccessful(
        currentAthleteModel,
        allAthleteModels,
        currentLift as never,
        meet?.isNational,
      ).placement;
    } catch {
      return null;
    }
  }, [allAthleteModels, currentAthleteModel, currentLift, meet?.isNational]);

  const nextLiftData = useMemo(() => {
    if (!currentAthleteModel || !currentLift || !currentGroup || !meet) {
      return null;
    }
    const next = getNextLiftDataWithGroupTransition({
      currentGroup: {
        id: currentGroup.id,
        plateauNumber: currentGroup.plateauNumber ?? null,
        groupNumber: currentGroup.groupNumber ?? null,
      },
      groups: schedulingView.groups,
      groupAthletes: currentGroupAthleteModels,
      allAthletes: allAthleteModels,
      currentAthlete: currentAthleteModel,
      currentLift: currentLift as never,
    });
    if (!next?.athlete || !next.lift) return null;

    const nextAthlete = athleteModelToAthleteWithAll(next.athlete);

    return {
      athlete: nextAthlete,
      movement: getExercise(next.lift as never) as
        | "squat"
        | "bench"
        | "deadlift",
      attemptNumber: getAttemptNumber(next.lift as never),
    };
  }, [
    allAthleteModels,
    schedulingView,
    currentAthleteModel,
    currentGroup,
    currentGroupAthleteModels,
    currentLift,
    meet,
  ]);

  const customLayoutRuntimeData = useMemo(
    () =>
      buildCustomDisciplineLayoutData({
        meet,
        currentGroup,
        groupTypes: customDisciplineGroupTypes,
        categoryReferences,
        referenceSources: {
          ageCategories: agecategories,
          weightCategories: weightcategories,
          records: meet?.records ? recordLocalToRecordRemote(meet.records) : [],
          levels: meet?.niveaux?.length
            ? levelLocalToLevelRemote(meet.niveaux)
            : levels,
          disciplines,
          leagues: ligues,
        },
      }),
    [
      meet,
      currentGroup,
      customDisciplineGroupTypes,
      categoryReferences,
      agecategories,
      weightcategories,
      levels,
      disciplines,
      ligues,
    ],
  );

  const language = getLanguage();
  const labelGroupTypeId = (
    currentGroup as GroupWithCustomDisciplineRuntime | null
  )?.customDisciplineGroup?.groupTypeId;
  const disciplineLabels = useMemo(
    () =>
      customDisciplineLabels(
        customDisciplineGroupTypes.find((type) => type.id === labelGroupTypeId),
        labelTranslations,
        language,
      ),
    [customDisciplineGroupTypes, labelGroupTypeId, labelTranslations, language],
  );
  const previousLayoutDataRef = useRef<InjectedData | undefined>(undefined);

  const layoutData = useMemo(() => {
    const layoutLevels =
      meet?.niveaux && meet.niveaux.length > 0
        ? levelLocalToLevelRemote(meet.niveaux)
        : levels;
    // Records already carry organisationId + nested organisation via adapter
    const layoutRecords = meet?.records
      ? recordLocalToRecordRemote(meet.records)
      : [];
    const organisation = resolveCompetitionOrganisation({
      meetOrganisationId: meet?.organisationId,
      records: layoutRecords,
    });
    const next = buildInjectedDataFromNest(
      {
        currentMeet: meet ?? null,
        currentAthlete: currentAthleteInGroup ?? null,
        athletes,
        currentGroup: currentGroup ?? null,
        groups: meet?.groups ?? [],
        records: layoutRecords,
        levels: layoutLevels,
        ageCategories: agecategories,
        weightCategories: weightcategories,
        disciplines,
        leagues: ligues,
        organisation,
        timer,
        timerSeconds,
        attemptTimers,
        attemptTimerSeconds,
        currentMovement,
        attemptNumber,
        placementIfSuccessful,
        refereeLights,
        displaySettings: {
          lightsEnabled: Boolean(refereeLights),
          showLights: true,
          webClientUrl: window.location.origin,
        },
        nextLiftData,
        themeColors,
      },
      previousLayoutDataRef.current,
    );
    if (customLayoutRuntimeData) {
      next.ageCategories = customLayoutRuntimeData.ageCategories;
      next.weightCategories = customLayoutRuntimeData.weightCategories;
      next.disciplineAgeCategories =
        customLayoutRuntimeData.disciplineAgeCategories;
      next.disciplineWeightCategories =
        customLayoutRuntimeData.disciplineWeightCategories;
      next.currentGroup =
        customLayoutRuntimeData.currentGroup as InjectedData["currentGroup"];
      next.groups = customLayoutRuntimeData.groups as InjectedData["groups"];
      next.athletes =
        customLayoutRuntimeData.athletes as InjectedData["athletes"];
      next.currentAthlete =
        customLayoutRuntimeData.currentAthlete as InjectedData["currentAthlete"];
      next.athlete = customLayoutRuntimeData.athlete ?? null;
    }
    next.__customDisciplineLabels = disciplineLabels;
    previousLayoutDataRef.current = next;
    return next;
  }, [
    disciplineLabels,
    athletes,
    attemptNumber,
    attemptTimerSeconds,
    attemptTimers,
    currentAthleteInGroup,
    currentGroup,
    customLayoutRuntimeData,
    currentMovement,
    agecategories,
    weightcategories,
    disciplines,
    ligues,
    meet,
    nextLiftData,
    placementIfSuccessful,
    refereeLights,
    levels,
    themeColors,
    timer,
    timerSeconds,
  ]);

  return {
    meet: meet ?? null,
    currentThemeName,
    currentLayoutDisciplineKey,
    themeColors,
    layoutData,
  };
}
